import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SendIcon, UsersIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Field, Toggle, inputClass } from '../../components/ui/Field';
import { eventTitle, formatDate, functionLabel, functionOrder, fullName, priorityClass, priorityLabel, voiceLabel, voiceOrder } from '../../utils/labels';
import type { ChoirFunction, Priority, Voice } from '../../types';
type Obseg = 'aktivni' | 'vsi' | 'glasovi' | 'funkcije' | 'dogodek' | 'rocno';
const obsegi: {
  key: Obseg;
  label: string;
}[] = [{
  key: 'aktivni',
  label: 'Vsi aktivni člani'
}, {
  key: 'vsi',
  label: 'Vsi člani in gostje'
}, {
  key: 'glasovi',
  label: 'Izbrane glasovne skupine'
}, {
  key: 'funkcije',
  label: 'Izbrane funkcije'
}, {
  key: 'dogodek',
  label: 'Udeleženci dogodka'
}, {
  key: 'rocno',
  label: 'Ročno izbrani člani'
}];
export function AdminAnnouncementForm() {
  const navigate = useNavigate();
  const {
    members,
    upcoming,
    expectedFor,
    addAnnouncement,
    pushToast,
    currentMember
  } = useApp();
  const [naslov, setNaslov] = useState('');
  const [besedilo, setBesedilo] = useState('');
  const [prioriteta, setPrioriteta] = useState<Priority>('obicajno');
  const [obseg, setObseg] = useState<Obseg>('aktivni');
  const [glasovi, setGlasovi] = useState<Voice[]>([]);
  const [funkcije, setFunkcije] = useState<ChoirFunction[]>([]);
  const [dogodekId, setDogodekId] = useState(upcoming[0]?.id ?? '');
  const [rocno, setRocno] = useState<string[]>([]);
  const [zahtevaPotrditev, setZahtevaPotrditev] = useState(false);
  const prejemniki = useMemo(() => {
    switch (obseg) {
      case 'aktivni':
        return members.filter(m => m.type === 'aktivni');
      case 'vsi':
        return members.filter(m => m.type === 'aktivni' || m.type === 'gostujoci');
      case 'glasovi':
        return members.filter(m => glasovi.includes(m.voice) && (m.type === 'aktivni' || m.type === 'gostujoci'));
      case 'funkcije':
        return members.filter(m => m.functions.some(f => funkcije.includes(f)));
      case 'dogodek':
        {
          const e = upcoming.find(x => x.id === dogodekId);
          return e ? expectedFor(e) : [];
        }
      case 'rocno':
        return members.filter(m => rocno.includes(m.id));
      default:
        return [];
    }
  }, [obseg, members, glasovi, funkcije, dogodekId, rocno, upcoming, expectedFor]);
  const obcinstvoLabel = useMemo(() => {
    switch (obseg) {
      case 'aktivni':
        return 'Vsi aktivni člani';
      case 'vsi':
        return 'Vsi člani in gostje';
      case 'glasovi':
        return glasovi.map(v => voiceLabel[v]).join(', ') || 'Izbrane glasovne skupine';
      case 'funkcije':
        return funkcije.map(f => functionLabel[f]).join(', ') || 'Izbrane funkcije';
      case 'dogodek':
        {
          const e = upcoming.find(x => x.id === dogodekId);
          return e ? `Udeleženci: ${eventTitle(e)}` : 'Udeleženci dogodka';
        }
      case 'rocno':
        return `Ročni izbor (${rocno.length})`;
      default:
        return '';
    }
  }, [obseg, glasovi, funkcije, dogodekId, rocno, upcoming]);
  function poslji() {
    addAnnouncement({
      id: `a${Math.random().toString(36).slice(2, 7)}`,
      naslov: naslov || 'Obvestilo zbora',
      besedilo,
      prioriteta,
      avtor: currentMember ? fullName(currentMember) : 'Vodstvo zbora',
      objavljeno: new Date().toISOString(),
      obcinstvoLabel,
      prejemniki: prejemniki.map(m => m.id),
      zahtevaPotrditev,
      prebrali: []
    });
    pushToast(`Obvestilo je bilo poslano ${prejemniki.length} članom.`);
    navigate('/admin/obvestila');
  }
  return <div className="mx-auto max-w-5xl space-y-4" data-id="element-465" data-mp="39kolld0ujc3z">
      <button onClick={() => navigate('/admin/obvestila')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-466" data-mp="39jviqn50d6r4">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-467" data-mp="164rzr7j4m1fs" />
        Nazaj na obvestila
      </button>

      <div data-id="element-468" data-mp="rfm3nsfl4rc4">
        <h1 className="text-2xl font-semibold text-ink" data-id="element-469" data-mp="rbpx91e0ozm7">Novo obvestilo</h1>
        <p className="mt-0.5 text-sm text-ink-muted" data-id="element-470" data-mp="wdf8ah18pdaa">
          Uradno sporočilo bo članom vidno v portalu, po potrebi z zahtevo za potrditev branja.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]" data-id="element-471" data-mp="1p1t8n3fswlp0">
        <Card className="space-y-4 p-5" data-id="element-472" data-mp="1oz6xyuxm5q2k">
          <Field label="Naslov" htmlFor="naslov" data-id="element-473" data-mp="1b0qjghtiev9v">
            <input id="naslov" value={naslov} onChange={e => setNaslov(e.target.value)} placeholder="Npr. Vaja prestavljena na soboto" className={inputClass} data-id="element-474" data-mp="1b44lfoy9h2gs" />
          </Field>

          <Field label="Sporočilo" htmlFor="besedilo" data-id="element-475" data-mp="3ozutyxrb312">
            <textarea id="besedilo" rows={9} value={besedilo} onChange={e => setBesedilo(e.target.value)} placeholder="Napišite jasno in kratko sporočilo članom…" className={inputClass} data-id="element-476" data-mp="3s9tj395uofdv" />
          </Field>

          <div data-id="element-477" data-mp="141ysqer7wyz">
            <p className="mb-2 text-[13px] font-medium text-ink" data-id="element-478" data-mp="13iptz730ix3">Prioriteta</p>
            <div className="flex flex-wrap gap-2" data-id="element-479" data-mp="39jc3m6a13gag">
              {(['obicajno', 'pomembno', 'nujno', 'sprememba'] as Priority[]).map(p => <button key={p} type="button" onClick={() => setPrioriteta(p)} aria-pressed={prioriteta === p} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${prioriteta === p ? 'border-green-500 bg-green-500 text-white' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-480" data-mp="34m2766o824d4">
                  {priorityLabel[p]}
                </button>)}
            </div>
          </div>

          <Toggle checked={zahtevaPotrditev} onChange={setZahtevaPotrditev} label="Zahtevaj potrditev branja" opis="Člani bodo morali klikniti „Prebral/a sem“." data-id="element-481" data-mp="3kagpguqf0rn4" />
        </Card>

        <div className="space-y-4" data-id="element-482" data-mp="2skcchh4zj6vx">
          <Card className="space-y-4 p-5" data-id="element-483" data-mp="2sldnjlm03tdn">
            <Field label="Prejemniki" htmlFor="obseg" data-id="element-484" data-mp="2wkcpcxcfra0v">
              <select id="obseg" value={obseg} onChange={e => setObseg(e.target.value as Obseg)} className={inputClass} data-id="element-485" data-mp="2wmyvqtnlbjhi">
                {obsegi.map(o => <option key={o.key} value={o.key} data-id="element-486" data-mp="gu9owx3araik">
                    {o.label}
                  </option>)}
              </select>
            </Field>

            {obseg === 'glasovi' && <div className="flex flex-wrap gap-2" data-id="element-487" data-mp="26b592rbvbo9r">
                {voiceOrder.filter(v => v !== 'brez').map(v => <button key={v} type="button" onClick={() => setGlasovi(g => g.includes(v) ? g.filter(x => x !== v) : [...g, v])} aria-pressed={glasovi.includes(v)} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${glasovi.includes(v) ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-488" data-mp="1wl2i0wob4ke2">
                      {voiceLabel[v]}
                    </button>)}
              </div>}

            {obseg === 'funkcije' && <div className="flex flex-wrap gap-2" data-id="element-489" data-mp="2bx3eqfoi0nlc">
                {functionOrder.map(f => <button key={f} type="button" onClick={() => setFunkcije(x => x.includes(f) ? x.filter(y => y !== f) : [...x, f])} aria-pressed={funkcije.includes(f)} className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out ${funkcije.includes(f) ? 'border-green-300 bg-green-50 text-green-700' : 'border-line bg-white text-ink-muted hover:bg-canvas'}`} data-id="element-490" data-mp="2gtk6djuwpk36">
                    {functionLabel[f]}
                  </button>)}
              </div>}

            {obseg === 'dogodek' && <Field label="Dogodek" htmlFor="dogodek" data-id="element-491" data-mp="3ii4pa6k0winx">
                <select id="dogodek" value={dogodekId} onChange={e => setDogodekId(e.target.value)} className={inputClass} data-id="element-492" data-mp="16w3t8qhup468">
                  {upcoming.map(e => <option key={e.id} value={e.id} data-id="element-493" data-mp="1gojb7nt5dsr4">
                      {formatDate(e.datum)} — {eventTitle(e)}
                    </option>)}
                </select>
              </Field>}

            {obseg === 'rocno' && <div className="max-h-56 overflow-y-auto viva-scroll rounded-lg border border-line p-2" data-id="element-494" data-mp="1ll9t8goiiyf6">
                {members.filter(m => m.type === 'aktivni' || m.type === 'gostujoci').map(m => <label key={m.id} className="flex items-center gap-2 px-2 py-1.5 text-[13px] text-ink" data-id="element-495" data-mp="57jpjb1b2c62">
                      <input type="checkbox" className="h-4 w-4 rounded border-line accent-green-500" checked={rocno.includes(m.id)} onChange={() => setRocno(r => r.includes(m.id) ? r.filter(x => x !== m.id) : [...r, m.id])} data-id="element-496" data-mp="8ov1btz6z64" />
                      {fullName(m)}
                    </label>)}
              </div>}

            <div className="flex items-center gap-2 rounded-lg bg-canvas px-3 py-2.5" data-id="element-497" data-mp="3tauen7lxkype">
              <UsersIcon className="h-4 w-4 text-green-600" data-id="element-498" data-mp="3p8h9akb3j2t5" />
              <p className="text-[13px] text-ink" data-id="element-499" data-mp="3pb3p17854omg">
                <span className="font-semibold" data-id="element-500" data-mp="3khhfhnx9x0uv">{prejemniki.length}</span> prejemnikov · {obcinstvoLabel}
              </p>
            </div>
          </Card>

          <Card className="p-5" data-id="element-501" data-mp="3fhd3ob7b8uaz">
            <h2 className="text-base font-semibold text-ink" data-id="element-502" data-mp="gtfodgad3p2d">Predogled</h2>
            <div className="mt-3 rounded-lg border border-line p-4" data-id="element-503" data-mp="c2h6wddn0kxj">
              <Badge className={priorityClass[prioriteta]} data-id="element-504" data-mp="c15z82h2mt15">{priorityLabel[prioriteta]}</Badge>
              <p className="mt-2 text-sm font-semibold text-ink" data-id="element-505" data-mp="jfge14i2nbo2">{naslov || 'Naslov obvestila'}</p>
              <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink-muted" data-id="element-506" data-mp="ocyqh4vas90t">
                {besedilo || 'Vsebina obvestila se bo prikazala tukaj.'}
              </p>
              {zahtevaPotrditev && <p className="mt-3 rounded-md bg-canvas px-2.5 py-1.5 text-[12px] text-ink-muted" data-id="element-507" data-mp="12chlcvz3lnv6">
                  Član bo videl gumb „Prebral/a sem“.
                </p>}
            </div>
          </Card>

          <div className="flex gap-2" data-id="element-508" data-mp="1c1remf5ohckv">
            <Button variant="secondary" full onClick={() => navigate('/admin/obvestila')} data-id="element-509" data-mp="1c2t07dkblhgu">
              Prekliči
            </Button>
            <Button full onClick={poslji} disabled={!besedilo || prejemniki.length === 0} data-id="element-510" data-mp="1l2zbcssjq5cu">
              <SendIcon className="h-4 w-4" data-id="element-511" data-mp="1l14p9b5jg776" />
              Pošlji obvestilo
            </Button>
          </div>
        </div>
      </div>
    </div>;
}